/**
 * The support tickets filed against this project.
 *
 * Read-only about the ticket itself, the way `ProjectInvoices` is read-only
 * about an invoice: the conversation is owned by the support section, and a
 * reply box in a second place is a second place for a reply to be lost. The
 * subject is a link into the thread, and the row is the one the queue draws -
 * same status tone, same priority tone, same assignee resolution - so a ticket
 * does not look like a different record depending on where it was found.
 *
 * The one thing this tab writes is the project link. `Ticket.projectId` is a
 * loose column the API takes on create and on PATCH, and no ticket form in this
 * app draws a project field, so the link has nowhere else to be made; see
 * `LinkToProjectModal`. Unlinking is the button on the row, so a ticket only
 * leaves a project where the person doing it can see which one they are taking
 * off.
 *
 * Unlike contracts, a ticket-to-project link is NOT checked against the client:
 * `checkTicketRefs` (api, `crm-support/lib/ticketShape.ts`) validates the client,
 * lead and call references and says nothing about the project. That is right for
 * this record - a voicemail ticket often has no client at all, only a lead or a
 * bare caller name - so the picker offers every unlinked ticket rather than
 * narrowing to one customer.
 *
 * ## Two permissions, not one
 *
 * The tab sits inside `/projects`, which answers to `manage:crm.projects`.
 * Writing a ticket answers to `manage:crm.support` (`tickets.routes.ts`), and
 * the second does not follow from the first, so the link and unlink controls are
 * absent - not disabled - without it. A button that renders and then 403s reads
 * as a broken feature rather than as one this operator does not have.
 *
 * ## The empty state is the normal state
 *
 * Sixteen tickets exist in production and not one of them names a project, so
 * every operator meets the empty card first. It is an `EmptyState` with a
 * sentence and a way forward, and "still loading" is a bare spinner with no
 * words in it: the two must not be confusable at a glance.
 */
import { useCallback, useEffect, useState } from "react";
import { LifeBuoy, Link2, Loader2, MessageSquareText } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import { getTickets, ticketParty, updateTicket, type Ticket } from "../../../lib/support";
import { useCrmUser } from "../../shell/CrmUser";
import { Avatar, Btn, Card, CardHead, EmptyState, StatusPill, fmtStamp, listCard, useToast } from "../../ui";
import { useStaff } from "../_shared/staff";
import { priorityTone, staffName, statusTone } from "../support/tone";
import { LinkToProjectModal, UnlinkCell, type LinkCandidate } from "./LinkToProjectModal";

/** Subject and number, status, priority, assignee, created, and the unlink
 *  control. The queue's client column is dropped: it is answered by the project
 *  head one card up, and repeating it down the page says nothing new. */
const COLS = "minmax(0,2.4fr) .95fr .95fr 1fr .9fr 44px";

/** The list route's own ceiling. A project carrying more than a hundred tickets
 *  is a support problem rather than a paging one, so this tab does not page. */
const MAX_ROWS = 100;

export function ProjectTickets({
  projectId,
  onChange,
}: {
  projectId: string;
  /** Tells the page frame that the count on this tab's pill is now stale. */
  onChange?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const canManage = userCanPlatform(useCrmUser(), "manage:crm.support");
  const { staff } = useStaff();
  // Null while the first read is in flight, so the spinner and the empty card
  // are two states and not one.
  const [rows, setRows] = useState<Ticket[] | null>(null);
  const [linking, setLinking] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getTickets({ projectId, pageSize: MAX_ROWS });
      setRows(res.rows);
    } catch {
      // Said out loud, because the empty card below cannot say it: "no tickets
      // are linked to this project" is exactly what a failed read would draw
      // otherwise, and it is also the true answer for every project today - so
      // nobody would ever doubt it.
      setRows([]);
      toast(t("common.error"));
    }
  }, [projectId, toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Every ticket the search finds, flattened into the shape the dialog draws.
  // The dialog drops the ones already sitting on a project.
  const candidates = useCallback(
    async (q: string): Promise<LinkCandidate[]> => {
      const res = await getTickets({ q: q || undefined, pageSize: MAX_ROWS });
      return res.rows.map((tk) => ({
        id: tk.id,
        title: tk.subject,
        number: tk.number,
        statusLabel: t(`support.statusLabel.${tk.status}`),
        tone: statusTone(tk.status),
        projectId: tk.projectId,
      }));
    },
    [t],
  );

  async function unlink(tk: Ticket) {
    setBusyId(tk.id);
    try {
      await updateTicket(tk.id, { projectId: null });
      // Dropped locally rather than reloaded: the row is leaving this list and
      // nothing else on the card is read off the server's copy of it.
      setRows((prev) => (prev ?? []).filter((r) => r.id !== tk.id));
      // The pill above this card counts the same rows, out of the summary. It is
      // not recomputed from the list, so a detach that did not say so would leave
      // a number over a card that disagrees with it for the rest of the session.
      onChange?.();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusyId(null);
    }
  }

  const linkBtn = canManage ? (
    <Btn small tone="brand" onClick={() => setLinking(true)}>
      <Link2 className="size-4" />
      {t("projects.linkTicket")}
    </Btn>
  ) : null;

  return (
    <>
      <Card style={listCard(COLS)}>
        <CardHead title={t("projects.linkedTickets")} actions={linkBtn} />

        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={LifeBuoy}
            title={t("projects.noTickets")}
            body={t("projects.noTicketsBody")}
            action={linkBtn}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
              <span>{t("support.subject")}</span>
              <span>{t("support.statusCol")}</span>
              <span>{t("support.priority")}</span>
              <span>{t("support.assignee")}</span>
              <span>{t("support.created")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((tk) => {
                // The party is a client OR a lead, and the avatar falls back to
                // the bare contact name: a voicemail ticket usually has neither
                // of the first two, and an initialled blank reads as broken.
                const party = ticketParty(tk);
                return (
                  // A div, not a link: the row ends in a button, and interactive
                  // content inside an <a> swallows its clicks. The subject link
                  // stretches an ::after over the row instead.
                  <div key={tk.id} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
                    <span className="flex min-w-0 items-center gap-3">
                      <Avatar name={party?.name ?? tk.contactName ?? tk.subject} />
                      <span className="min-w-0">
                        <Link
                          to={`/support/${tk.id}`}
                          className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full"
                        >
                          {tk.subject}
                        </Link>
                        <span className="crm-prow-s crm-trunc flex items-center gap-2">
                          <span className="crm-tnum">{tk.number}</span>
                          {tk._count ? (
                            <span className="inline-flex items-center gap-1">
                              <MessageSquareText className="size-3" />
                              <span className="crm-tnum">{tk._count.messages}</span>
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </span>

                    <span>
                      <StatusPill tone={statusTone(tk.status)}>{t(`support.statusLabel.${tk.status}`)}</StatusPill>
                    </span>
                    <span>
                      <StatusPill tone={priorityTone(tk.priority)}>
                        {t(`support.priorityLabel.${tk.priority}`)}
                      </StatusPill>
                    </span>
                    <span className="crm-mut crm-trunc text-[12.5px]">{staffName(staff, tk.assignedTo)}</span>
                    {/* Created rather than updated: this tab answers "what came
                        in against this project", and the queue is the screen
                        that tracks what moved most recently. */}
                    <span className="crm-mut crm-tnum text-[12.5px]">{fmtStamp(tk.createdAt)}</span>

                    <UnlinkCell
                      can={canManage}
                      busy={busyId === tk.id}
                      disabled={busyId !== null}
                      onClick={() => void unlink(tk)}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {linking ? (
        <LinkToProjectModal
          title={t("projects.linkTicket")}
          searchPlaceholder={t("support.search")}
          fetchCandidates={candidates}
          link={(id) => updateTicket(id, { projectId })}
          onClose={() => setLinking(false)}
          onLinked={() => {
            void load();
            onChange?.();
          }}
        />
      ) : null}
    </>
  );
}
